// Sources: where every layer comes from, and whether it is currently updating.
//
// One card per publisher, not one row per feed. A publisher fails as a unit —
// five Traffic Scotland layers go dark together — so the traffic light belongs
// on the organisation, with a smaller light per feed inside it. A reader can
// then tell "nothing is happening" from "we have stopped being told", and tell
// at a glance which publisher is at fault.
//
// A separate Vite entry: it shares no code with the map, so it adds nothing to
// the map's bundle and loads in a few kB.
//
// Note this still answers a different question from the Live Status card on the
// map. Here green means "we are successfully talking to this source", not "all
// is well in Aberdeen". "No power outages" and "the power feed died an hour
// ago" look identical on the map; only this page separates them.

import './status-page.css';
import { initAnalytics } from './analytics.ts';
import { SOURCE_NOTES } from './sources.ts';
import { CADENCE, LOGOS, PUBLISHER, REGIONS } from './publishers.ts';
import { escapeHtml } from './escape.ts';
import { hideBrokenImages } from './hide-broken-images.ts';

type FeedState = 'live' | 'stale' | 'down' | 'disabled' | 'pending';

interface FeedHealth {
  id: string;
  label: string;
  section: string;
  state: FeedState;
  lastSuccessAt: number;
  ageSeconds: number | null;
  expectedEverySeconds: number | null;
  items: number;
  error: string;
  needsKey: boolean;
  hasKey: boolean;
  upstream: string;
  licence: string;
  note: string;
}

interface Diagnostics {
  generatedAt: number;
  uptimeSeconds: number;
  /** Absent on a backend deployed before build reporting existed. */
  build?: { version: string; commit: string };
  sections: { section: string; feeds: FeedHealth[] }[];
  summary: Record<FeedState, number>;
}

import { BASE } from './http.ts';

// "No key" and "no source" both mean the same thing to a reader — this feed is
// not wired up — and which credential is involved is nobody's business on a
// public page, so the two states share one honest label.
const STATE_LABEL: Record<FeedState, string> = {
  live: 'live',
  stale: 'stale',
  down: 'down',
  disabled: 'not set up',
  pending: 'not set up',
};

/** Worst first: a dead feed outranks a stale one outranks an unbuilt one. */
const RANK: Record<FeedState, number> = {
  down: 3, stale: 2, disabled: 1, pending: 1, live: 0,
};

/** Bare length of time, e.g. "12m". */
function span(seconds: number): string {
  if (seconds < 60) return `${Math.max(0, Math.round(seconds))}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  if (seconds < 86_400) return `${Math.round(seconds / 3600)}h`;
  return `${Math.round(seconds / 86_400)}d`;
}

function ago(seconds: number | null): string {
  return seconds === null ? 'never' : `${span(seconds)} ago`;
}

/**
 * The organisation a feed belongs to. Unmapped feeds — a provider added before
 * anyone updates publishers.ts — get a card of their own named after their
 * upstream host, so a new feed appears unstyled rather than not at all.
 */
function publisherOf(f: FeedHealth): string {
  const host = f.upstream.split(/[\s,(]/)[0];
  return PUBLISHER[f.id] ?? (host || f.label);
}

function cadence(f: FeedHealth): string {
  const baked = CADENCE[f.id];
  if (baked) return baked;
  return f.expectedEverySeconds !== null && Number.isFinite(f.expectedEverySeconds)
    ? `refreshes every ${span(f.expectedEverySeconds)}`
    : 'no feed to refresh';
}

function logoHtml(name: string): string {
  const logo = LOGOS[name];
  if (!logo) return `<span class="logo-placeholder">${escapeHtml(name)}</span>`;
  // alt carries the name because the logo is the only place the card says it.
  const size = logo.fit === 'emblem' ? 'height="26"' : 'width="100" height="26"';
  return `<img class="logo ${logo.fit}" src="${escapeHtml(logo.src)}"
    alt="${escapeHtml(name)}" ${size} loading="lazy" />`;
}

/**
 * One feed's line: dot, label, age, cadence. The detail the old table carried
 * per row — description, caveat, operational note, error — moves to a hover
 * title: still there for whoever is debugging a dead feed, off the page for
 * the reader the redesign is for.
 */
function feedRow(f: FeedHealth): string {
  const note = SOURCE_NOTES[f.id];
  const title = [note?.description, note?.caveat, f.note, f.error]
    .filter(Boolean).join('\n');
  return `
    <div class="feed st-${f.state}" ${title ? `title="${escapeHtml(title)}"` : ''}>
      <span class="dot"></span>
      <span class="feed-label">${escapeHtml(f.label)}</span>
      <span class="age">${ago(f.ageSeconds)}</span>
      <span class="cadence">${escapeHtml(cadence(f))}</span>
    </div>`;
}

function card(name: string, feeds: FeedHealth[]): string {
  // The same worst-first rule as the grid, inside the card: the stale camera
  // feed is the reason the card is amber, so it should not be hiding at the
  // bottom of five green rows. Stable, so healthy feeds keep payload order.
  feeds = [...feeds].sort((a, b) => RANK[b.state] - RANK[a.state]);
  const worst = feeds.reduce((a, b) => (RANK[b.state] > RANK[a.state] ? b : a));
  const licences = [...new Set(feeds.map((f) => f.licence).filter(Boolean))];
  const region = REGIONS[name];
  // "no open feed" is for a publisher with nothing wired up. A live feed whose
  // publisher states no licence terms just has no licence line — saying "no
  // open feed" under working feeds would read as a fault.
  const unbuilt = feeds.every((f) => f.state === 'disabled' || f.state === 'pending');
  const licenceLine = licences.length
    ? `<span class="licences">${escapeHtml(licences.join(' · '))}</span>`
    : unbuilt ? '<span class="licences">no open feed</span>' : '';
  const foot = region || licenceLine ? `
      <div class="card-foot">
        ${region ? `<span class="region">${escapeHtml(region)}</span>` : ''}
        ${licenceLine}
      </div>` : '';
  return `
    <article class="card st-${worst.state}">
      <div class="card-head">
        <span class="logo-box">${logoHtml(name)}</span>
        <span class="chip"><span class="dot"></span>${STATE_LABEL[worst.state]}</span>
      </div>
      <div class="feeds">${feeds.map(feedRow).join('')}</div>${foot}
    </article>`;
}

/**
 * Which build of each half is serving.
 *
 * The frontend and the backend deploy through separate pipelines — Cloudflare
 * builds this bundle from GitHub, a workflow ships the container to Hetzner —
 * so they are routinely a minute apart during a rollout, and permanently apart
 * when one half fails. Reporting both, and saying so when they disagree, is the
 * only way to tell "still deploying" from "half of it never landed".
 */
function renderBuild(d: Diagnostics): void {
  const el = document.getElementById('build');
  if (!el) return;

  const api = d.build;
  const site = __BUILD__;
  const known = (c: string | undefined) => Boolean(c) && c !== 'unknown';
  // Only a claim worth making when both ends actually know their commit.
  const mismatch = known(site.commit) && known(api?.commit) && site.commit !== api!.commit;

  const part = (label: string, b: { version: string; commit: string } | undefined) => (b
    ? `${escapeHtml(label)} <strong>v${escapeHtml(b.version)}</strong>
       <code>${escapeHtml(b.commit)}</code>`
    : `${escapeHtml(label)} <span class="dim">unreported</span>`);

  el.innerHTML = `
    ${part('site', site)} · ${part('api', api)}
    ${mismatch ? '<span class="warn">— halves are on different commits, a deploy may still be running</span>' : ''}`;
}

function render(d: Diagnostics): void {
  const out = document.getElementById('out')!;
  const sub = document.getElementById('sub')!;
  renderBuild(d);

  sub.innerHTML = `${d.summary.live} live · ${d.summary.stale} stale
    · ${d.summary.down} down · ${d.summary.disabled + d.summary.pending} not set up
    — backend up ${span(d.uptimeSeconds)},
    checked ${new Date(d.generatedAt).toLocaleTimeString('en-GB')}`;

  // Group by publisher, keeping the payload's feed order within each card.
  const byPublisher = new Map<string, FeedHealth[]>();
  for (const f of d.sections.flatMap((s) => s.feeds)) {
    const name = publisherOf(f);
    if (!byPublisher.has(name)) byPublisher.set(name, []);
    byPublisher.get(name)!.push(f);
  }

  // Worst card first, ties broken by how much it publishes — a broken Traffic
  // Scotland beats a broken single feed to the top of the grid.
  const cards = [...byPublisher]
    .map(([name, feeds]) => ({
      name,
      feeds,
      rank: Math.max(...feeds.map((f) => RANK[f.state])),
    }))
    .sort((a, b) => (b.rank - a.rank) || (b.feeds.length - a.feeds.length));

  out.innerHTML = cards.map((c) => card(c.name, c.feeds)).join('');
}

async function tick(): Promise<void> {
  try {
    const res = await fetch(`${BASE}/api/v1/diagnostics`, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    render(await res.json() as Diagnostics);
  } catch (err) {
    document.getElementById('sub')!.innerHTML =
      `<span class="warn">Cannot reach the backend: ${escapeHtml(
        err instanceof Error ? err.message : String(err))}</span>`;
  }
}

initAnalytics();
hideBrokenImages();
void tick();
// Frequent enough to watch a feed recover while you are fixing it.
setInterval(() => { if (!document.hidden) void tick(); }, 10_000);
